<!-- @format -->

<template>
    <MapLoader
        v-if="isReady && isMounted"
        ref="mapLoader"
        :map-config="mapConfig"
        class="gst-hotels-map u-height-100">
        <template v-slot="{ mapsApi, map }">
            <HotelsNoData
                v-if="resultsNotFound && !isNewSearchArea"
                class="gst-hotels-map__empty-state"
                @reset-filters="resetFilters" />
            <HotelsMapActions
                v-if="isOpenInventoryOrDynamicFlow"
                :map="map"
                class="gst-hotels-map__actions" />
            <MapMarkerLocation
                v-if="showVenueMarker"
                :maps-api="mapsApi"
                :map="map"
                :location="getLocation" />
            <MapMarkerHotel
                v-for="hotel in hotels"
                :key="hotel.id"
                :maps-api="mapsApi"
                :map="map"
                :hotel="hotel"
                :is-focused="focusedHotel && focusedHotel.id === hotel.id"
                :is-active="selectedHotel && selectedHotel.id === hotel.id"
                :bundle-products="getFinalBundleProductsForHotelRoom(hotel)"
                @click.prevent="onHotelMarkerClickDo(hotel)" />
            <MapPopupHotelCard
                v-if="selectedHotel"
                :maps-api="mapsApi"
                :map="map"
                :config="popupConfig"
                :hotel="selectedHotel"
                @open="onPopupOpenDo($event)"
                @closed="unselectHotel()" />
        </template>
    </MapLoader>
</template>

<script>
import isEqual from 'lodash/isEqual';
import getDistance from 'geolib/es/getDistance';
import { mapState, mapGetters, mapActions } from 'vuex';
import { getQueryParamsCurrent } from '@core/utils/windowLocationUtils.js';
import { hasLocation } from '@core/utils/cityObjectUtils';
import { getDistanceUnitOfMeasureForCountry } from '@core/utils/measureUtils';
import { create as CreateBundleProductsModel } from '@tenant/app/modelsV2/BundleProductsModel';
import { create as CreateBundleProductsHotelReservationModel } from '@tenant/app/modelsV2/BundleProductsHotelReservationModel';
import MapLoader from '@tenant/app/components/map/MapLoader.vue';
import MapMarkerLocation from '@tenant/app/components/map/MapMarkerLocation.vue';
import MapMarkerHotel from '@tenant/app/components/map/MapMarkerHotel.vue';
import MapPopupHotelCard from '@tenant/app/components/map/MapPopupHotelCard.vue';
import HotelsListCard from '@tenant/app/modules/hotel/features/addHotelReservation/AddHotelReservation/HotelsListCard.vue';
import HotelsMapActions from '@tenant/app/modules/hotel/features/addHotelReservation/AddHotelReservation/HotelsMapActions.vue';
import mapConstants from '@tenant/app/utils/constants/map';
import hotelsConstants from '@tenant/app/utils/constants/hotels';
import locationsConstants from '@tenant/app/utils/constants/locations';
import EventFlowMixin from '../mixins/EventFlowMixin';
import HotelsNoData from './HotelsNoData';

export default {
    name: 'HotelsMap',
    components: {
        MapLoader,
        MapMarkerLocation,
        MapMarkerHotel,
        MapPopupHotelCard,
        HotelsMapActions,
        HotelsNoData,
    },
    mixins: [EventFlowMixin],
    props: {
        event: {
            type: Object,
            required: true,
        },
        busEventsParent: {
            type: Object,
            default: null,
        },
        bundleProducts: {
            type: Object,
            required: true,
            default: function () {
                return CreateBundleProductsModel();
            },
        },
        isVisible: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['highlight-hotel-reservation', 'open-hotel-card', 'close-hotel-card'],
    data() {
        return {
            isReady: false,
            isMounted: false,
            mapConfig: null,
            popupConfig: null,
            selectedHotel: null,
            mapBounds: {
                minLat: null,
                maxLat: null,
                minLng: null,
                maxLng: null,
            },
        };
    },
    computed: {
        ...mapState({
            hotels: (state) => state.addHotelReservationState.hotels.list,
            focusedHotel: (state) => state.addHotelReservationState.hotels.focusedHotel,
            location: (state) => state.addHotelReservationState.hotels.filters.location,
            itemsNotFound: (state) => state.addHotelReservationState.hotels.itemsNotFound,
            closestCityNotFound: (state) => state.addHotelReservationState.closestCityNotFound,
        }),
        ...mapGetters({
            isNewSearchArea: 'addHotelReservationState/map/isNewSearchArea',
            getLocation: 'addHotelReservationState/getLocation',
        }),
        resultsNotFound() {
            return this.itemsNotFound || this.closestCityNotFound;
        },
        distanceUnitOfMeasure() {
            return getDistanceUnitOfMeasureForCountry(this.location.countryCode);
        },
        isValidCoordinatesLocation() {
            return this.location.latitude && this.location.longitude;
        },
        showVenueMarker() {
            return (
                hasLocation(this.isDynamicFlow ? this.getLocation : this.location) ||
                !this.isCityCentric
            );
        },
        bundleProductsFinal() {
            const ret = this.bundleProducts._clone();

            ret.setHotelReservation(
                CreateBundleProductsHotelReservationModel(ret.hotelReservation.hotel)
            );

            return ret;
        },
        fitBoundsPadding() {
            return this.$vuetify.breakpoint.mdAndUp ? 100 : 50;
        },
        isCityCentric() {
            const locationType = this.isDynamicFlow ? this.getLocation?.type : this.location?.type;

            return (locationType ?? '') === locationsConstants.TYPES.CITY;
        },
        hotelIdQueryParam() {
            const queryParams = getQueryParamsCurrent();
            return queryParams.hotelId;
        },
    },
    watch: {
        hotels: {
            handler: function (hotelsList) {
                this.unselectHotel();
                if (hotelsList.length) {
                    this.initMapFromLocation({ preserveZoom: true });
                }
            },
            deep: true,
        },
        isVisible: function (newValue) {
            if (!newValue || this.hotelIdQueryParam) {
                this.unselectHotel();
            } else {
                // mobile devices need a map resize after it becomes visible
                // otherwise it will not stretch the map canvas on full height
                return this.$refs.mapLoader?.mapResize();
            }
        },
        /**
         * Location watcher. If the location is changed the map will center on that location
         * before fetching the hotels. this way the costly animation from one location to
         * another is removed.
         */
        location: {
            handler: function (newLocation, oldLocation) {
                this.unselectHotel();
                if (!isEqual(newLocation, oldLocation)) {
                    if (newLocation.longitude && newLocation.latitude) {
                        this.initMapFromLocation({ preserveZoom: true });
                    } else {
                        this.initMapDefaults();
                    }
                }
            },
            deep: true,
        },
    },
    methods: {
        ...mapActions({
            resetFilters: 'addHotelReservationState/resetFilters',
        }),
        getFinalBundleProductsForHotelRoom(item) {
            const ret = this.bundleProducts._clone();

            ret.setHotelReservation(CreateBundleProductsHotelReservationModel(item));

            return ret;
        },
        onHotelMarkerClickDo(hotel) {
            this.selectedHotel = { ...hotel };
            this.popupConfig = {
                card: HotelsListCard,
                cardClasses: 'u-width-100',
                propsToBind: (item) => {
                    return {
                        item: item,
                        distanceUnitOfMeasure: this.distanceUnitOfMeasure,
                        inLocation: this.isDynamicFlow ? this.getLocation : this.location,
                        bundleProducts: this.getFinalBundleProductsForHotelRoom(item),
                        withTo: true,
                        width: this.$vuetify.breakpoint.mdAndUp ? '146px' : '138px',
                        height: this.$vuetify.breakpoint.mdAndUp ? '81px' : '138px',
                        addButtonLabel: this.$t('_common:buttons.add'),
                        busEventsParent: this.busEventsParent,
                        distanceFrom: this.isOpenInventoryFlow,
                        distanceToVenue: this.calculateDistanceToVenue(item),
                    };
                },
            };
        },
        selectHotelFromQueryParams() {
            this.$nextTick(() => {
                if (this.hotels.length <= hotelsConstants.HOTEL_ONLY.limit) {
                    const hotel = this.hotels.find(
                        (item) => item.id.toString() === this.hotelIdQueryParam.toString()
                    );
                    if (hotel) {
                        this.onHotelMarkerClickDo(hotel);
                    }
                }
            });
        },
        unselectHotel() {
            this.selectedHotel = null;
            this.$emit('close-hotel-card');
        },
        onPopupOpenDo(boundingRect) {
            this.$emit('open-hotel-card', boundingRect);
        },
        addCoordinatesToMapBounds(longitude, latitude) {
            if (!this.mapBounds.minLng || longitude < this.mapBounds.minLng) {
                this.mapBounds.minLng = longitude;
            }

            if (!this.mapBounds.maxLng || longitude > this.mapBounds.maxLng) {
                this.mapBounds.maxLng = longitude;
            }

            if (!this.mapBounds.minLat || latitude < this.mapBounds.minLat) {
                this.mapBounds.minLat = latitude;
            }

            if (!this.mapBounds.maxLat || latitude > this.mapBounds.maxLat) {
                this.mapBounds.maxLat = latitude;
            }
        },
        addLocationToMapBounds() {
            if (this.isValidCoordinatesLocation) {
                this.addCoordinatesToMapBounds(this.location.longitude, this.location.latitude);
            }
        },
        addEventVenueToMapBounds() {
            this.addCoordinatesToMapBounds(this.event.venueLongitude, this.event.venueLatitude);
        },
        addHotelsToMapBounds() {
            (this.hotels || []).forEach((hotel) =>
                this.addCoordinatesToMapBounds(hotel.longitude, hotel.latitude)
            );
            this.hotelIdQueryParam && this.selectHotelFromQueryParams();
        },
        updateMapConfigBounds() {
            this.mapConfig = {
                bounds: [
                    [this.mapBounds.minLng, this.mapBounds.minLat],
                    [this.mapBounds.maxLng, this.mapBounds.maxLat],
                ],
                fitBoundsOptions: {
                    padding: this.fitBoundsPadding,
                },
            };
        },
        resetMapBounds() {
            this.mapBounds = {
                minLat: null,
                maxLat: null,
                minLng: null,
                maxLng: null,
            };
        },
        /**
         * Initialize map configuration based on a searched location
         * Useful on mobile on the user's initial switch to map view
         */
        initMapFromLocation(config) {
            const defaultZoom = mapConstants.DEFAULT_CONFIG.ZOOM;
            const zoom = config?.preserveZoom ? this.getMapZoom() || defaultZoom : defaultZoom;

            this.resetMapBounds();
            // if we have hotels then set bounds to zoom the map to fit all
            // otherwise center on the new location with default zoom to eliminate animation.
            // very useful when a new location returns no hotels.
            if (this.hotels.length) {
                this.addLocationToMapBounds();
                this.addHotelsToMapBounds();
                this.updateMapConfigBounds();
            } else {
                this.mapConfig = {
                    center: [this.location.longitude, this.location.latitude],
                    zoom,
                };
            }
        },
        /**
         * Initialize map configuration based on a known event
         */
        initMapFromEvent() {
            this.addEventVenueToMapBounds();
            this.addHotelsToMapBounds();
            this.updateMapConfigBounds();
        },
        /**
         * Initialize map configuration from Prismic resource if it exists, otherwise it will default to New York
         */
        async initMapDefaults() {
            try {
                const mapDefaults = await this.$prismic.client.getSingle(
                    mapConstants.DEFAULT_CONFIG.PRISMIC.CUSTOM_TYPE
                );
                // Filter map by type
                const mapType = mapDefaults.data.locations.find(
                    (item) =>
                        item.type === mapConstants.DEFAULT_CONFIG.PRISMIC.MAP_TYPES.UNKNOWN_EVENT
                );
                this.mapConfig = {
                    center: [mapType.location.longitude, mapType.location.latitude],
                    zoom: mapType.zoom,
                };
            } catch (e) {
                this.mapConfig = {
                    center: [
                        mapConstants.DEFAULT_CONFIG.LONGITUDE,
                        mapConstants.DEFAULT_CONFIG.LATITUDE,
                    ],
                    zoom: mapConstants.DEFAULT_CONFIG.ZOOM,
                };
            }
        },
        async init() {
            if (this.event && this.event.id && !this.isValidCoordinatesLocation) {
                // if a known event is used the map will load with the venue in the center
                // and after that the hotels will be added
                this.initMapFromEvent();
            } else if (this.isValidCoordinatesLocation) {
                // Useful on mobile on the user's initial switch to map view
                // on unknown event flow or know event when locations was changed
                this.initMapFromLocation();
            } else {
                await this.initMapDefaults();
            }
            this.isReady = true;
        },
        getMapZoom() {
            return this.$refs.mapLoader?.map?.getZoom();
        },
        calculateDistanceToVenue(item) {
            if (this.isDynamicFlow && this.event.venueLatitude) {
                return (
                    getDistance(
                        { latitude: item.latitude, longitude: item.longitude },
                        {
                            latitude: this.event.venueLatitude,
                            longitude: this.event.venueLongitude,
                        }
                    ) / 1000
                );
            }
            return null;
        },
    },
    mounted() {
        this.isMounted = true;
    },
    created() {
        this.init();
    },
};
</script>

<style lang="scss" scoped>
@import '@scssVariables';
@import '@scssMixins';

.gst-hotels-map {
    position: relative;

    .gst-hotels-map__actions {
        position: absolute;
        top: 38px;
        left: 50%;
        z-index: $z-index-map-hotel-button;
        transform: translate(-50%);
    }
}

@include mobile-only {
    .gst-hotels-map {
        .gst-hotels-map__empty-state {
            position: absolute;
            top: 16px;
            left: 50%;
            width: 84%;
            padding: theme-spacing(4);
            background: theme-color('warning-alpha-15');
            transform: translate(-50%, 0);
        }

        .gst-hotels-map__actions {
            top: 20px;
        }
    }
}
</style>
